import { Injectable } from "@angular/core";
import { DatePipe } from "@angular/common";
import { BehaviorSubject } from "rxjs";
import { environment } from "../../environments/environment";
import { MsalService } from "@azure/msal-angular";
import { AccountWithGroupClaims } from "@app/models/account-with-group-claims";
import { getGroupsFromStorage } from "@app/utils/storage-utils";
import { UserType } from "@app/models/common";
import { WorkRequestService } from "./work-request.service";
import { NgxSpinnerService } from "ngx-spinner";
import { UserStatus } from "@app/models/work-request";

export type datediff  = {
    hours: number;
    days: number;
    minutes: number;
    seconds: number;   
}

@Injectable({
    providedIn: "root",
})
export class GlobalService {
    isPMUser = false;
    restrictedProjectCount = 0;
    isRestrictedPMUser = false;
    isSponsorUser = false;
    iframeScale: number = 1;
    statusReportScale : number = 1;

    constructor(private datepipe: DatePipe,
        private authService: MsalService,
        private workRequestService: WorkRequestService,
        private spinner: NgxSpinnerService,
    ) {}

    DATE_FORMAT = "yyyy-MM-dd";
    private storedObject : {[index: string] : any} = {};


    storeData(key: string, data: any){
        this.storedObject[key]=data;
    }
    getStoredData(key: string) : any {
        return this.storedObject[key];
    }


    updateItemInCache(key: string, idField: string, idValue: any, data: any) {
        if (Array.isArray(this.storedObject[key])) {
            const indexToUpdate = this.storedObject[key].findIndex((item: { [x: string]: any; })  => item[idField] === idValue);
            this.storedObject[key][indexToUpdate] = data;
            this.storedObject[key] = Object.assign([],  this.storedObject[key]);
        }
        /*
            for (let rec of this.storedObject[key]) {
                if (rec[idField] == idValue) {
                    rec = data;
                    break;
                }
            }
        }
        */
    }

    deleteItemFromCache(key: string, idField: string, idValue: any) {
        if (Array.isArray(this.storedObject[key])) {
            const indexToUpdate = this.storedObject[key].findIndex((item: { [x: string]: any; })  => item[idField] === idValue);
            //Deleting causes a gap in the data, so just blank it out.
            //delete  this.storedObject[key][indexToUpdate];
            this.storedObject[key][indexToUpdate] = {};
            this.storedObject[key] = Object.assign([],  this.storedObject[key]);
        }
    }
    hasStoredData(key: string) : boolean {
        return this.storedObject[key] !== undefined;
    }

    public customVariable = new BehaviorSubject<any>({
        customProperty: "original value",
    });


    getSystemDate() {
        if (environment.systemDate !== "") {
            const ts = new Intl.DateTimeFormat("en-US", {
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
                hour12: false,
            }).format(new Date());
            const timestamp = environment.systemDate + "T" + ts;
            try {
                return new Date(timestamp);
            } catch {
                return new Date();
            }
        } else {
            return new Date();
        }
    }

    getSystemDateUTC() {
        let dt =  this.getSystemDate();
        dt.setTime(dt.getTime() + new Date().getTimezoneOffset() * 60000);
        return dt;
    }
    convertDateArrayToString(arr: any) {
        let retStr = "";
        if (arr) {
            for (let i = 0; i < arr.length; i++) {
                if (i > 0) {
                    retStr += ",";
                }
                retStr += this.datepipe.transform(arr[i], this.DATE_FORMAT);
            }
            console.log(retStr);
        }
        return retStr;
    }

    /*
    Dates are stored in DATE_FORMAT, so we can split on '-'
    */
    convertDateStringToDateArray(dateStr: string) {
        const retDateArr = [];
        const strSplit = dateStr.split(",");
        for (let i = 0; i < strSplit.length; i++) {
            const d: string[] = strSplit[i].split("-");
            retDateArr.push(
                new Date(parseInt(d[0]), parseInt(d[1]) - 1, parseInt(d[2]))
            );
        }

        return retDateArr;
    }

    getFormattedDateYYYMMDD(date : Date) {
        const year = date.getFullYear();
      
        let month = (1 + date.getMonth()).toString();
        month = month.length > 1 ? month : '0' + month;
      
        let day = date.getDate().toString();
        day = day.length > 1 ? day : '0' + day;
      
        return year + month + day;
    }

    /**
     * TitleCase a camelCaseString 
     * ex: btnExport => Btn Export
     * @param input 
     * @returns 
     */
    titleCaseString(input: string) : string {
        const result = input.replace(/([A-Z])/g, " $1");
        const titleCase = result.charAt(0).toUpperCase() + result.slice(1);
        return titleCase.trim();
    }

    checkGroupAccess() : UserType{

        const userType : UserType = { isAdmin : false, isReadOnly: false, isUser: false}
        let activeAccount =
            this.authService.instance.getActiveAccount() as AccountWithGroupClaims;

        if (
            !activeAccount &&
            this.authService.instance.getAllAccounts().length > 0
        ) {
            activeAccount =
                this.authService.instance.getAllAccounts()[0] as AccountWithGroupClaims;
        }

        userType.isAdmin =
            activeAccount?.idTokenClaims?.groups?.includes(
                environment.appGroups.adminGroupId
            ) ||
            getGroupsFromStorage(activeAccount)?.includes(
                environment.appGroups.adminGroupId
            ) ||
            false;

        userType.isReadOnly =
            activeAccount?.idTokenClaims?.groups?.includes(
                environment.appGroups.readOnlyGroupId
            ) ||
            getGroupsFromStorage(activeAccount)?.includes(
                environment.appGroups.readOnlyGroupId
            ) ||
            false;
            
        userType.isUser =
            activeAccount?.idTokenClaims?.groups?.includes(
                environment.appGroups.userGroupId
            ) ||
            getGroupsFromStorage(activeAccount)?.includes(
                environment.appGroups.userGroupId
            ) ||
            false;

        //let hasRequiredEmail = false;
        for (const entry of environment.emailAccounts.users) {
            if (activeAccount.username.toLowerCase().indexOf(entry.toLowerCase()) > -1) {
                userType.isUser = true;
                break;
            }
        }

        return userType;
    }

    //TODO: performance vs functionality.  Add a promise to know when this is done? then pm guard and sponsor guard could be used.
    //make an endpoint where retrieve all this information in one call. (userstatus struct)
    
    //We nest the calls instead of parallel because we want to try to catch token errors in first call.
    checkUserAttributes() {
        this.spinner.show();

        /*
        this.workRequestService.isUserPM().subscribe({
            next: (resp: any) => {
                this.isPMUser = resp;
                console.log("Is PM?:" + this.isPMUser);
                this.workRequestService.isUserRestrictedPM().subscribe({
                    next: (resp: any) => {
                        this.isRestrictedPMUser = resp;
                        console.log("Is Restricted PM?:" + this.isRestrictedPMUser);
                    },
                    error: () => {
                        this.spinner.hide();
                        console.log("Error getting isPM" + resp.headers.get('WWW-Authenticate'));
                    },
                });
                this.workRequestService.isUserSponsor().subscribe({
                    next: (resp: any) => {
                        this.isSponsorUser = resp;
                        this.spinner.hide();
                        console.log("Is Sponsor?:" + this.isSponsorUser);
                    },
                    error: () => {
                        this.spinner.hide();
                        console.log("Error getting isSponsor Status" + resp.headers.get('WWW-Authenticate'));
                    },
                });
                this.workRequestService.getRestrictedProjectCounts().subscribe({
                    next: (resp: any) => {
                        this.restrictedProjectCount = resp;
                        console.log("Number of restricted projects: " + this.restrictedProjectCount);
                    },
                    error: () => {
                        this.spinner.hide();
                        console.log("Error getting isPM" + resp.headers.get('WWW-Authenticate'));
                    },
                });
            },
        */
        this.workRequestService.getUserStatus().subscribe({
                next: (resp: UserStatus) => {
                    this.isPMUser = resp.isPM;
                    this.isRestrictedPMUser = resp.isRestrictedPM
                    this.isSponsorUser = resp.isSponsor;
                    this.restrictedProjectCount = resp.restrictedProjectCount;
                    console.log('User Status', JSON.stringify(resp));
                    this.spinner.hide();
                },
            error: (resp: any) => {
                this.spinner.hide();
                console.log("Error getting PM Status" + resp.headers.get('WWW-Authenticate'));
            },
        });

    }

 

    public dateDiff(to : Date, from : Date): datediff {
        const differenceInMilliseconds = to.getTime() - from.getTime();
        let dateDiff: datediff = {

            days : Math.floor(differenceInMilliseconds / (1000 * 60 * 60 * 24)),
            hours : Math.floor(differenceInMilliseconds / (1000 * 60 * 60)),
            minutes : Math.floor(differenceInMilliseconds / (1000 * 60)),
            seconds : Math.floor(differenceInMilliseconds / (1000 )),

        }
        return dateDiff;
    }

    public camelCaseToWords(str: string) {
        return str
          .replace(/^[a-z]/g, char => ` ${char.toUpperCase()}`)
          .replace(/[A-Z]|[0-9]+/g, ' $&')
          .replace(/(?:\s+)/, char => '');
      };


    public getFileTimestamp(dt?: Date) : string {
        let currentDate = new Date();
        if (dt) {
            currentDate = dt;
        }
        let timestamp = currentDate.getFullYear() + "_" +
            ("0" + (currentDate.getMonth() + 1)).slice(-2) + "_" +
            ("0" + currentDate.getDate()).slice(-2) + "_" +
            ("0" + currentDate.getHours()).slice(-2) +
            ("0" + currentDate.getMinutes()).slice(-2) +
            ("0" + currentDate.getSeconds()).slice(-2);
        return timestamp;
    }
}
